import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  CheckCircle2,
  Clock3,
  ExternalLink,
  Link2,
  Loader2,
  Megaphone,
  PenLine,
  Send,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  getSocialReviewProgram,
  listMySocialReviews,
  submitSocialReview,
  type SocialReviewPlatform,
  type SocialReviewStatus,
  type SocialReviewSubmission,
} from "@/lib/boide-api";

export const Route = createFileRoute("/_authenticated/cam-nhan")({
  head: () => ({
    meta: [
      { title: "Chia Sẻ Cảm Nhận Nhận Thưởng - Bói Đê" },
      {
        name: "description",
        content:
          "Đăng bài cảm nhận về Bói Đê lên mạng xã hội, gửi link bài viết và nhận thưởng 1.000.000đ vào ví bói khi bài được duyệt.",
      },
    ],
  }),
  component: CamNhanPage,
});

const money = (n: number) => `${(n || 0).toLocaleString("vi-VN")}đ`;

const DEFAULT_REWARD = 1_000_000;

const fmtDate = (s: string) => {
  try {
    return new Date(s).toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

const PLATFORM_OPTIONS: {
  value: SocialReviewPlatform;
  label: string;
  hosts: string[];
}[] = [
  { value: "facebook", label: "Facebook", hosts: ["facebook.com", "fb.com", "fb.watch"] },
  { value: "tiktok", label: "TikTok", hosts: ["tiktok.com"] },
  { value: "instagram", label: "Instagram", hosts: ["instagram.com"] },
  { value: "threads", label: "Threads", hosts: ["threads.net", "threads.com"] },
  { value: "other", label: "Nền tảng khác", hosts: [] },
];

const STATUS_META: Record<
  SocialReviewStatus,
  { label: string; className: string; icon: typeof Clock3 }
> = {
  pending: {
    label: "Chờ duyệt",
    className: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400",
    icon: Clock3,
  },
  approved: {
    label: "Đã duyệt",
    className: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    icon: CheckCircle2,
  },
  rejected: {
    label: "Từ chối",
    className: "border-destructive/40 bg-destructive/10 text-destructive",
    icon: XCircle,
  },
};

/** Trả về hostname đã bỏ "www." nếu URL hợp lệ (http/https), ngược lại null. */
function parseHostname(raw: string): string | null {
  try {
    const url = new URL(raw.trim());
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    return url.hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

function detectPlatform(hostname: string): SocialReviewPlatform | null {
  for (const opt of PLATFORM_OPTIONS) {
    if (opt.hosts.some((h) => hostname === h || hostname.endsWith(`.${h}`))) {
      return opt.value;
    }
  }
  return null;
}

function CamNhanPage() {
  const program = useQuery({
    queryKey: ["social-review-program"],
    queryFn: getSocialReviewProgram,
    // Config chỉ tinh chỉnh số tiền/trạng thái — không chặn render form.
    // Không retry để không kẹt skeleton khi BE tạm lỗi/chưa sẵn sàng.
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const reward = 1_000_000; // Cứng 1 triệu, BE chỉ tinh chỉnh số tiền thưởng tối đa, không thay đổi logic.
  // Render form ngay (lạc quan); chỉ ẩn khi BE khẳng định chương trình đã tắt.
  const isPaused = program.data?.is_active === false;

  return (
    <main className="app-feature-page flex-1 min-h-0 overflow-y-auto px-3 py-4 pb-24 md:px-8 md:py-8">
      <div className="mx-auto max-w-3xl space-y-5">
        <header className="space-y-1">
          <h1 className="flex items-center gap-2 font-display text-2xl text-gold-gradient md:text-3xl">
            <Megaphone className="h-6 w-6 text-primary" /> Chia sẻ cảm nhận — nhận thưởng
          </h1>
          <p className="text-sm text-muted-foreground">
            Đăng bài cảm nhận về Bói Đê lên mạng xã hội, gửi link cho chúng tôi. Bài được duyệt nhận
            đến <b className="text-primary">{money(reward)}</b> vào ví bói, tùy lượt tương tác.
          </p>
        </header>

        <ProgramCard reward={reward} criteriaNote={program.data?.criteria_note} />

        {isPaused ? (
          <Card className="border-gold-soft/40 bg-card/60">
            <CardContent className="py-4 text-sm text-muted-foreground">
              <Megaphone className="mr-2 inline h-4 w-4 text-primary/70" />
              Chương trình đang <b className="text-primary">tạm dừng nhận bài</b>. Vui lòng quay lại
              sau hoặc theo dõi thông báo trong Hộp thư.
            </CardContent>
          </Card>
        ) : (
          <SubmitCard />
        )}

        <HistoryCard />
      </div>
    </main>
  );
}

function ProgramCard({ reward, criteriaNote }: { reward: number; criteriaNote?: string | null }) {
  const steps = [
    {
      icon: PenLine,
      title: "1. Đăng bài",
      desc: "Viết cảm nhận thật của bạn về Bói Đê trên Facebook, TikTok, Instagram, Threads... ở chế độ công khai.",
    },
    {
      icon: Link2,
      title: "2. Gửi link",
      desc: "Dán link bài viết vào form bên dưới và gửi cho Bói Đê.",
    },
    {
      icon: CheckCircle2,
      title: "3. Nhận thưởng",
      desc: `Kết quả duyệt báo qua Hộp thư trong tối đa 8 ngày. Bài đạt nhận từ 10.000đ đến ${money(reward)} vào ví bói, tùy lượt tương tác.`,
    },
  ];

  return (
    <Card className="border-gold-soft/40">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Thể lệ chương trình</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          {steps.map((s) => (
            <div
              key={s.title}
              className="rounded-xl border border-border/45 bg-background/50 p-3 dark:border-white/8 dark:bg-black/20"
            >
              <s.icon className="mb-1.5 h-4 w-4 text-primary" />
              <p className="text-sm font-semibold text-foreground">{s.title}</p>
              <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{s.desc}</p>
            </div>
          ))}
        </div>

        <details className="group rounded-xl border border-border/45 bg-background/50 p-3 dark:border-white/8 dark:bg-black/20">
          <summary className="cursor-pointer select-none text-sm font-semibold text-foreground">
            Tiêu chí duyệt bài & điều khoản
          </summary>
          <ul className="mt-2 list-disc space-y-1.5 pl-5 text-xs leading-relaxed text-muted-foreground">
            <li>
              Bài viết ở chế độ <b className="text-foreground/90">công khai</b>, còn tồn tại tại
              thời điểm duyệt và giữ tối thiểu <b className="text-foreground/90">30 ngày</b> sau khi
              nhận thưởng.
            </li>
            <li>
              Nội dung <b className="text-foreground/90">tự viết, từ 100 từ</b> (hoặc video từ 30
              giây) chia sẻ trải nghiệm thật của bạn; không sao chép bài của người khác.
            </li>
            <li>
              Bài có gắn link <b className="text-foreground/90">boide.online</b> và/hoặc hashtag{" "}
              <b className="text-foreground/90">#BoiDe</b>. Khuyến khích kèm mã giới thiệu của bạn
              để nhận thêm hoa hồng.
            </li>
            <li>
              Bài đáp ứng các điều kiện trên nhận{" "}
              <b className="text-foreground/90">ít nhất 10.000đ</b>.
            </li>
            <li>
              Bài có <b className="text-foreground/90">trên 50 lượt tương tác</b>{" "}
              (reaction/share/comment/repost) nhận{" "}
              <b className="text-foreground/90">ít nhất 50.000đ</b>.
            </li>
            <li>
              Bài có <b className="text-foreground/90">trên 100 lượt tương tác</b> nhận{" "}
              <b className="text-foreground/90">ít nhất 100.000đ</b>.
            </li>
            <li>
              Bài có <b className="text-foreground/90">trên 1000 lượt tương tác</b> nhận{" "}
              <b className="text-foreground/90">ít nhất 500.000đ</b>.
            </li>
            <li>
              Lưu ý: Bói Đê sẽ không duyệt các bài viết có dấu hiệu spam tương tác, nội dung không
              liên quan, hoặc vi phạm chính sách mạng xã hội.
            </li>
            <li>
              Tùy nội dung bài viết, Bói Đê quyết định mức thưởng cụ thể và đảm bảo không thấp hơn
              mức tối thiểu của từng khung tương tác.
            </li>
            <li>Mỗi link bài viết chỉ được ghi nhận thưởng một lần trên toàn hệ thống.</li>
            <li>
              Thưởng cộng vào <b className="text-foreground/90">ví bói</b> (dùng cho các dịch vụ
              trong app), không quy đổi tiền mặt, không chuyển nhượng.
            </li>
            <li>
              Kết quả duyệt (đạt/không đạt kèm lý do) gửi qua Hộp thư trong tối đa{" "}
              <b className="text-foreground/90">8 ngày</b> kể từ khi gửi link.
            </li>
            {criteriaNote ? <li>{criteriaNote}</li> : null}
          </ul>
        </details>
      </CardContent>
    </Card>
  );
}

function SubmitCard() {
  const qc = useQueryClient();
  const [platform, setPlatform] = useState<SocialReviewPlatform>("facebook");
  const [postUrl, setPostUrl] = useState("");
  const [note, setNote] = useState("");

  const hostname = useMemo(() => parseHostname(postUrl), [postUrl]);
  const isUrlValid = hostname !== null;
  const detected = hostname ? detectPlatform(hostname) : null;
  const platformMismatch =
    isUrlValid && platform !== "other" && detected !== null && detected !== platform;

  const submitMut = useMutation({
    mutationFn: () =>
      submitSocialReview({
        platform,
        post_url: postUrl.trim(),
        note: note.trim() || undefined,
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["my-social-reviews"] });
      setPostUrl("");
      setNote("");
      toast.success(res.message || "Đã gửi bài viết, chờ duyệt trong tối đa 8 ngày.");
    },
    onError: (e: Error) => toast.error(e.message || "Gửi thất bại, vui lòng thử lại."),
  });

  const handleUrlChange = (value: string) => {
    setPostUrl(value);
    // Tự nhận diện nền tảng theo domain để user đỡ phải chọn tay
    const host = parseHostname(value);
    if (host) {
      const auto = detectPlatform(host);
      if (auto) setPlatform(auto);
    }
  };

  return (
    <Card className="border-gold-soft/40">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Gửi link bài viết</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <select
          value={platform}
          onChange={(e) => setPlatform(e.target.value as SocialReviewPlatform)}
          className="w-full rounded-lg border border-gold-soft/40 bg-background/40 px-3 py-2 text-sm"
        >
          {PLATFORM_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <div className="space-y-1">
          <input
            value={postUrl}
            onChange={(e) => handleUrlChange(e.target.value)}
            placeholder="Dán link bài viết, VD: https://www.facebook.com/..."
            inputMode="url"
            className="w-full rounded-lg border border-gold-soft/40 bg-background/40 px-3 py-2 text-sm"
          />
          {postUrl.trim() && !isUrlValid ? (
            <p className="text-xs text-destructive">
              Link chưa hợp lệ — hãy dán địa chỉ đầy đủ bắt đầu bằng https://
            </p>
          ) : null}
          {platformMismatch ? (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Link có vẻ không thuộc nền tảng đã chọn — kiểm tra lại trước khi gửi nhé.
            </p>
          ) : null}
        </div>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Ghi chú thêm (không bắt buộc), VD: tên tài khoản MXH của bạn..."
          rows={3}
          className="w-full rounded-lg border border-gold-soft/40 bg-background/40 px-3 py-2 text-sm"
        />
        <Button
          className="gap-2"
          onClick={() => submitMut.mutate()}
          disabled={submitMut.isPending || !isUrlValid}
        >
          {submitMut.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
          Gửi bài viết
        </Button>
      </CardContent>
    </Card>
  );
}

function HistoryCard() {
  const query = useQuery({
    queryKey: ["my-social-reviews"],
    queryFn: listMySocialReviews,
    retry: false,
  });
  const items = query.data?.items ?? [];

  return (
    <Card className="border-gold-soft/40">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Bài viết đã gửi</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {query.isLoading ? <Skeleton className="h-20 w-full rounded-xl" /> : null}
        {!query.isLoading && items.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground">
            Bạn chưa gửi bài viết nào. Gửi bài đầu tiên để nhận thưởng nhé!
          </p>
        ) : null}
        {items.map((item) => (
          <SubmissionRow key={item.id} item={item} />
        ))}
      </CardContent>
    </Card>
  );
}

function SubmissionRow({ item }: { item: SocialReviewSubmission }) {
  const meta = STATUS_META[item.status] ?? STATUS_META.pending;
  const StatusIcon = meta.icon;
  const platformLabel =
    PLATFORM_OPTIONS.find((o) => o.value === item.platform)?.label ?? item.platform;

  return (
    <div className="rounded-xl border border-border/45 bg-background/50 p-3 dark:border-white/8 dark:bg-black/20">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            {platformLabel} · {fmtDate(item.created_at)}
          </p>
          <a
            href={item.post_url}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 truncate text-sm text-foreground/90 underline-offset-2 hover:text-primary hover:underline"
          >
            <span className="truncate">{item.post_url}</span>
            <ExternalLink className="h-3.5 w-3.5 shrink-0" />
          </a>
        </div>
        <Badge variant="outline" className={`shrink-0 gap-1 ${meta.className}`}>
          <StatusIcon className="h-3 w-3" />
          {meta.label}
        </Badge>
      </div>
      {item.status === "approved" ? (
        <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">
          +{money(item.reward_amount ?? DEFAULT_REWARD)} — nhận thưởng trong Hộp thư nếu chưa nhận.
        </p>
      ) : null}
      {item.status === "rejected" && item.admin_note ? (
        <p className="mt-2 text-xs text-muted-foreground">Lý do: {item.admin_note}</p>
      ) : null}
    </div>
  );
}
